import type {
  BusbarObject,
  DomainGuid,
  GridElementIdentity,
  LoadFlowScenarioElementCalculation,
  LoadFlowScenarioResults
} from '../../../..';
import type {
  BusbarEvaluatedVoltage,
  BusbarEvaluatedVoltageDiffTransformerEndPercents
} from '../../../../derived-state/loadFlowSummary';
import type { Degree } from '../../../../domain/Degree';
import type { Percent } from '../../../../domain/Percent';
import type { Voltage } from '../../../../domain/Voltage';
import type { ForStoringAttentionStatus } from '../../../AttentionStatusRepository';
import { type EvaluatedResult } from '../../../EvaluatedResult';
import {
  selectByType,
  selectByTypeFromAllScenarios
} from '../../../selectByType';
import { BusbarSummaryEvaluation } from './busbarSummaryEvaluation';
import type { ForBusbarEvaluation } from './forBusbarEvaluation';

export class BusbarEvaluation {
  private constructor(
    private readonly busbars: BusbarObject[],
    private readonly evaluationEventHandler: ForStoringAttentionStatus
  ) {}

  static from(
    loadFlowScenarios: LoadFlowScenarioResults[],
    evaluationEventHandler: ForStoringAttentionStatus
  ) {
    const results = selectByTypeFromAllScenarios('Busbar', loadFlowScenarios);
    return new BusbarEvaluation(results, evaluationEventHandler);
  }

  static fromResults(
    loadFlowResults: LoadFlowScenarioElementCalculation[],
    evaluationEventHandler: ForStoringAttentionStatus
  ) {
    const results = selectByType('Busbar', loadFlowResults);
    return new BusbarEvaluation(results, evaluationEventHandler);
  }

  private get busbarResults() {
    return this.busbars.map(busbar => busbar.result);
  }

  evaluateSummary(
    evaluationEngine: ForBusbarEvaluation
  ): BusbarEvaluationResult {
    const summaryEvaluation = new BusbarSummaryEvaluation(
      this.busbarResults,
      evaluationEngine
    );

    const voltageResult = summaryEvaluation.evaluateVoltages();
    const voltageDiffTransformerEndPercentResult =
      summaryEvaluation.evaluateVoltageDiffTransformerEndPercents();

    return { voltageResult, voltageDiffTransformerEndPercentResult };
  }

  evaluateEachBusbar(evaluationEngine: ForBusbarEvaluation): EvaluatedBusbar[] {
    return this.busbars.map(busbar => {
      return this.evaluateBusbar(evaluationEngine, busbar);
    });
  }

  private evaluateBusbar(
    evaluationEngine: ForBusbarEvaluation,
    busbar: BusbarObject
  ): EvaluatedBusbar {
    const messenger = this.evaluationEventHandler.delegateMessenger(
      busbar.id,
      busbar.objectType
    );
    const voltage = evaluationEngine.evaluateVoltage(
      busbar.result.voltage,
      messenger
    );
    const voltageDiffPercentValue =
      busbar.result.voltageDiffTransformerEndPercent !== undefined
        ? Math.abs(busbar.result.voltageDiffTransformerEndPercent)
        : busbar.result.voltageDiffTransformerEndPercent;

    const voltageDiffPercent =
      evaluationEngine.evaluateVoltageDiffTransformerEndPercent(
        voltageDiffPercentValue,
        messenger
      );

    const voltageCosinePhi = evaluationEngine.evaluateVoltageCosinePhi(
      busbar.result.voltageCosinePhi,
      messenger
    );
    const voltageDiffNominal = evaluationEngine.evaluateVoltageDiffNominal(
      busbar.result.voltageDiffNominal,
      messenger
    );
    const voltageDiffTransformer =
      evaluationEngine.evaluateVoltageDiffTransformerEnd(
        busbar.result.voltageDiffTransformerEnd,
        messenger
      );

    return {
      id: busbar.id,
      objectType: busbar.objectType,
      results: {
        voltage,
        voltageDiffPercent,
        voltageCosinePhi,
        voltageDiffNominal,
        voltageDiffTransformer
      }
    };
  }

  getEvaluationEvents() {
    return this.evaluationEventHandler.toState();
  }
}

export type BusbarEvaluationResult = {
  voltageResult: BusbarEvaluatedVoltage;
  voltageDiffTransformerEndPercentResult: BusbarEvaluatedVoltageDiffTransformerEndPercents;
};

export type EvaluatedBusbar = GridElementIdentity & {
  id: DomainGuid;
  objectType: 'Busbar';
  results: {
    voltage: EvaluatedResult<Voltage>;
    voltageDiffPercent: EvaluatedResult<Percent>;
    voltageCosinePhi: EvaluatedResult<Degree>;
    voltageDiffNominal: EvaluatedResult<Voltage>;
    voltageDiffTransformer: EvaluatedResult<Voltage>;
  };
};
